import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { inboxTypeReadable } from '@wapharma/domain';
import { getToken } from '../auth';
import { isCatalogImportInboxType } from '../components/inboxCatalogImport';
import { openSettingsOverlay } from '../lib/settingsOverlay';
import { lotDetailPath } from '../lib/lotPaths';

/** Minimal inbox shape for navigation (home + flyout). */
export type InboxOpenTarget = {
  type: string;
  entityId: string;
};

export type InboxNavigate = (path: string) => void;

export type InboxOpenAccess = {
  grants?: readonly string[];
  roles?: readonly string[];
  packs?: readonly string[];
};

/**
 * Resolve navigation for an inbox entity. Async only for SELLING_LOT_ADVANCED.
 * Sync paths return immediately; async calls `nav` when ready.
 * Unreadable domains are skipped (no redirect, no 403).
 */
export function openInboxEntity(
  i: InboxOpenTarget,
  nav: InboxNavigate,
  access?: InboxOpenAccess,
): void {
  if (
    access &&
    !inboxTypeReadable(i.type, access.grants, access.roles ?? [], access.packs)
  ) {
    return;
  }
  if (
    i.type === 'SUPPORT_TICKET_OPEN' ||
    i.type === 'SUPPORT_TICKET_REPLY' ||
    i.type === 'SUPPORT_TICKET_URGENT'
  ) {
    nav(`/support/${encodeURIComponent(i.entityId)}`);
    return;
  }
  if (i.type === 'PO_ORDERED') {
    nav(`/procurement/purchase-orders/${encodeURIComponent(i.entityId)}`);
    return;
  }
  if (i.type === 'CONVENTION_STAMP_REQUESTED') {
    nav(`/conventions/${encodeURIComponent(i.entityId)}`);
    return;
  }
  if (i.type === 'ORDER_PENDING') {
    nav(`/orders/${encodeURIComponent(i.entityId)}`);
    return;
  }
  if (i.type === 'B2B_ORDER_DRAFT') {
    nav(`/b2b/sales-orders/${encodeURIComponent(i.entityId)}`);
    return;
  }
  if (i.type === 'SELLING_LOT_ADVANCED' || i.type === 'LOT_PRICE_DEVIATION') {
    const token = getToken();
    if (!token) {
      nav('/catalog');
      return;
    }
    void apiGet<{ data: { productId: string } }>(
      `/inventory/variants/${encodeURIComponent(i.entityId)}/selling-lot`,
      token,
    )
      .then((raw) => {
        const data = unwrapData(raw) as { productId?: string };
        if (data?.productId) {
          nav(`/catalog/products/${encodeURIComponent(data.productId)}?tab=lots`);
          return;
        }
        nav('/catalog');
      })
      .catch(() => nav('/catalog'));
    return;
  }
  if (i.type === 'BATCH_NEAR_EXPIRY') {
    nav(lotDetailPath(i.entityId));
    return;
  }
  if (isCatalogImportInboxType(i.type)) {
    nav('/catalog');
    return;
  }
  if (i.type === 'GTIN_SOURCE_UPDATE') {
    openSettingsOverlay('data-sources');
    return;
  }
  nav('/catalog');
}
